// src/pages/customer-panel/leaderboard/LeaderboardPage.js
// Leaderboard page with podium and full ranking table

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Avatar,
    Chip,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    Button,
    Skeleton,
    Tooltip,
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import StarIcon from '@mui/icons-material/Star';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#10b981';

const RANK_COLORS = {
    1: { bg: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: '#f59e0b', label: 'Gold', shadow: 'rgba(245,158,11,0.3)' },
    2: { bg: 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)', color: '#94a3b8', label: 'Silver', shadow: 'rgba(148,163,184,0.3)' },
    3: { bg: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)', color: '#d97706', label: 'Bronze', shadow: 'rgba(217,119,6,0.3)' },
};

function getInitials(name) {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.charAt(0).toUpperCase();
}

function getAvatarColor(name) {
    const colors = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#06b6d4', '#ef4444', '#f97316'];
    let hash = 0;
    for (let i = 0; i < (name || '').length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
}

// Podium card for top 3
const PodiumCard = ({ entry, rank }) => {
    const rankConfig = RANK_COLORS[rank] || RANK_COLORS[3];
    const name = entry.name || entry.student_name || entry.user_name || 'Student';
    const points = entry.points || entry.score || entry.xp || 0;
    const level = entry.level || entry.rank_level || '';
    const badgesCount = entry.badges_count || entry.badges_earned || entry.badges || 0;
    const avatarUrl = entry.avatar || entry.profile_image || entry.avatar_url;

    // Heights for podium effect
    const heights = { 1: 200, 2: 170, 3: 150 };
    const sizes = { 1: 80, 2: 68, 3: 60 };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                order: rank === 1 ? 1 : rank === 2 ? 0 : 2,
            }}
        >
            {/* Avatar + Rank badge */}
            <Box sx={{ position: 'relative', mb: 1.5 }}>
                <Avatar
                    src={avatarUrl}
                    sx={{
                        width: sizes[rank],
                        height: sizes[rank],
                        fontSize: sizes[rank] * 0.35,
                        fontFamily,
                        fontWeight: 700,
                        bgcolor: avatarUrl ? 'transparent' : getAvatarColor(name),
                        border: `3px solid ${rankConfig.color}`,
                        boxShadow: `0 8px 24px ${rankConfig.shadow}`,
                    }}
                >
                    {!avatarUrl && getInitials(name)}
                </Avatar>
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: -8,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        background: rankConfig.bg,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: `0 4px 12px ${rankConfig.shadow}`,
                        border: '2px solid white',
                    }}
                >
                    <Typography sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: '0.75rem' }}>
                        {rank}
                    </Typography>
                </Box>
            </Box>

            {/* Name */}
            <Typography
                sx={{
                    fontFamily,
                    fontWeight: 700,
                    color: '#1e293b',
                    fontSize: rank === 1 ? '1rem' : '0.9rem',
                    textAlign: 'center',
                    maxWidth: 120,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    mb: 0.5,
                }}
            >
                {name}
            </Typography>

            {/* Points */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <StarIcon sx={{ fontSize: 16, color: rankConfig.color }} />
                <Typography sx={{ fontFamily, fontWeight: 700, color: rankConfig.color, fontSize: '0.95rem' }}>
                    {points.toLocaleString()}
                </Typography>
            </Box>

            {/* Level */}
            {level && (
                <Chip
                    label={level}
                    size="small"
                    sx={{
                        fontFamily,
                        fontWeight: 600,
                        fontSize: '0.65rem',
                        bgcolor: `${rankConfig.color}15`,
                        color: rankConfig.color,
                        height: 22,
                        mb: 1,
                    }}
                />
            )}

            {/* Podium bar */}
            <Box
                sx={{
                    width: { xs: 90, sm: 120 },
                    height: heights[rank],
                    background: rankConfig.bg,
                    borderRadius: '16px 16px 0 0',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pt: 2,
                }}
            >
                {rank === 1 && <EmojiEventsIcon sx={{ fontSize: 36, color: 'rgba(255,255,255,0.8)' }} />}
                {rank === 2 && <MilitaryTechIcon sx={{ fontSize: 32, color: 'rgba(255,255,255,0.8)' }} />}
                {rank === 3 && <WorkspacePremiumIcon sx={{ fontSize: 30, color: 'rgba(255,255,255,0.8)' }} />}
                {badgesCount > 0 && (
                    <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.7)', fontSize: '0.7rem', mt: 1 }}>
                        {badgesCount} badge{badgesCount !== 1 ? 's' : ''}
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

export default function LeaderboardPage() {
    const [leaderboard, setLeaderboard] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;
    const currentUserId = user?.id || user?._id || user?.profile?.id;

    const fetchLeaderboard = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const response = await axiosInstance.get('/lms/portal/leaderboard');
            const data = response.data;
            setLeaderboard(data.leaderboard || data.data || data.results || data.rankings || []);
        } catch (err) {
            console.error('Failed to fetch leaderboard:', err);
            setError(err.response?.data?.message || 'Failed to load leaderboard.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchLeaderboard();
    }, [fetchLeaderboard]);

    const top3 = leaderboard.slice(0, 3);
    const rest = leaderboard.slice(3);

    const isCurrentUser = (entry) => {
        if (!currentUserId) return false;
        return (
            entry.user_id === currentUserId ||
            entry.id === currentUserId ||
            entry._id === currentUserId
        );
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    borderRadius: { xs: 0, md: '0 0 24px 24px' },
                    px: { xs: 2, md: 4 },
                    py: { xs: 3, md: 4 },
                    mb: 3,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TrendingUpIcon sx={{ fontSize: 40, color: 'white' }} />
                    <Box>
                        <Typography
                            variant="h4"
                            sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.5rem', md: '2rem' } }}
                        >
                            Leaderboard
                        </Typography>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem' }}>
                            See how you rank among your peers
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1000, mx: 'auto' }}>
                {/* Error */}
                {error && (
                    <Alert
                        severity="error"
                        sx={{ mb: 3, borderRadius: '12px', fontFamily }}
                        action={
                            <Button color="inherit" size="small" onClick={fetchLeaderboard} sx={{ fontFamily, textTransform: 'none' }}>
                                Retry
                            </Button>
                        }
                    >
                        {error}
                    </Alert>
                )}

                {loading ? (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <CircularProgress sx={{ color: ACCENT }} />
                        <Typography sx={{ fontFamily, color: '#64748b', mt: 2 }}>Loading leaderboard...</Typography>
                    </Box>
                ) : leaderboard.length === 0 ? (
                    <Paper
                        sx={{
                            py: 8,
                            px: 3,
                            textAlign: 'center',
                            borderRadius: '16px',
                            border: '1px solid #e2e8f0',
                            boxShadow: 'none',
                        }}
                    >
                        <TrendingUpIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                        <Typography variant="h6" sx={{ fontFamily, fontWeight: 700, color: '#1e293b', mb: 1 }}>
                            No leaderboard data yet
                        </Typography>
                        <Typography sx={{ fontFamily, color: '#64748b' }}>
                            Start earning points to appear on the leaderboard!
                        </Typography>
                    </Paper>
                ) : (
                    <>
                        {/* Podium - Top 3 */}
                        {top3.length > 0 && (
                            <Paper
                                elevation={0}
                                sx={{
                                    borderRadius: '20px',
                                    border: '1px solid #e2e8f0',
                                    overflow: 'hidden',
                                    mb: 3,
                                    pb: 0,
                                }}
                            >
                                <Box sx={{ p: 3, textAlign: 'center' }}>
                                    <Typography variant="h6" sx={{ fontFamily, fontWeight: 700, color: '#1e293b', mb: 3 }}>
                                        Top Performers
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'flex-end',
                                            gap: { xs: 1, sm: 3 },
                                        }}
                                    >
                                        {top3.length >= 2 && <PodiumCard entry={top3[1]} rank={2} />}
                                        {top3.length >= 1 && <PodiumCard entry={top3[0]} rank={1} />}
                                        {top3.length >= 3 && <PodiumCard entry={top3[2]} rank={3} />}
                                    </Box>
                                </Box>
                            </Paper>
                        )}

                        {/* Full Leaderboard Table */}
                        <TableContainer
                            component={Paper}
                            elevation={0}
                            sx={{
                                borderRadius: '16px',
                                border: '1px solid #e2e8f0',
                            }}
                        >
                            <Table>
                                <TableHead>
                                    <TableRow
                                        sx={{
                                            '& th': {
                                                fontFamily,
                                                fontWeight: 700,
                                                color: '#64748b',
                                                fontSize: '0.8rem',
                                                bgcolor: '#f8fafc',
                                                borderBottom: '2px solid #e2e8f0',
                                                py: 1.5,
                                            },
                                        }}
                                    >
                                        <TableCell align="center" sx={{ width: 60 }}>Rank</TableCell>
                                        <TableCell>Student</TableCell>
                                        <TableCell align="center">Points</TableCell>
                                        <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Level</TableCell>
                                        <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>Badges</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {leaderboard.map((entry, index) => {
                                        const rank = entry.rank || index + 1;
                                        const name = entry.name || entry.student_name || entry.user_name || 'Student';
                                        const points = entry.points || entry.score || entry.xp || 0;
                                        const level = entry.level || entry.rank_level || '';
                                        const badgesCount = entry.badges_count || entry.badges_earned || entry.badges || 0;
                                        const avatarUrl = entry.avatar || entry.profile_image || entry.avatar_url;
                                        const isCurrent = isCurrentUser(entry);
                                        const rankConfig = RANK_COLORS[rank];

                                        return (
                                            <TableRow
                                                key={entry.id || entry._id || entry.user_id || index}
                                                sx={{
                                                    bgcolor: isCurrent ? `${ACCENT}08` : 'transparent',
                                                    border: isCurrent ? `2px solid ${ACCENT}` : 'none',
                                                    '& td': {
                                                        fontFamily,
                                                        py: 1.5,
                                                        borderBottom: '1px solid #f1f5f9',
                                                    },
                                                    '&:hover': {
                                                        bgcolor: isCurrent ? `${ACCENT}12` : '#f8fafc',
                                                    },
                                                    transition: 'background-color 0.2s',
                                                }}
                                            >
                                                {/* Rank */}
                                                <TableCell align="center">
                                                    {rankConfig ? (
                                                        <Box
                                                            sx={{
                                                                width: 30,
                                                                height: 30,
                                                                borderRadius: '50%',
                                                                background: rankConfig.bg,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                mx: 'auto',
                                                            }}
                                                        >
                                                            <Typography
                                                                sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: '0.8rem' }}
                                                            >
                                                                {rank}
                                                            </Typography>
                                                        </Box>
                                                    ) : (
                                                        <Typography sx={{ fontFamily, fontWeight: 600, color: '#64748b' }}>
                                                            {rank}
                                                        </Typography>
                                                    )}
                                                </TableCell>

                                                {/* Student */}
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                        <Avatar
                                                            src={avatarUrl}
                                                            sx={{
                                                                width: 36,
                                                                height: 36,
                                                                fontSize: '0.85rem',
                                                                fontFamily,
                                                                fontWeight: 700,
                                                                bgcolor: avatarUrl ? 'transparent' : getAvatarColor(name),
                                                            }}
                                                        >
                                                            {!avatarUrl && getInitials(name)}
                                                        </Avatar>
                                                        <Box>
                                                            <Typography
                                                                sx={{
                                                                    fontFamily,
                                                                    fontWeight: 600,
                                                                    color: '#1e293b',
                                                                    fontSize: '0.88rem',
                                                                }}
                                                            >
                                                                {name}
                                                                {isCurrent && (
                                                                    <Chip
                                                                        label="You"
                                                                        size="small"
                                                                        sx={{
                                                                            fontFamily,
                                                                            fontWeight: 700,
                                                                            fontSize: '0.6rem',
                                                                            height: 20,
                                                                            ml: 1,
                                                                            bgcolor: `${ACCENT}15`,
                                                                            color: ACCENT,
                                                                        }}
                                                                    />
                                                                )}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </TableCell>

                                                {/* Points */}
                                                <TableCell align="center">
                                                    <Typography
                                                        sx={{
                                                            fontFamily,
                                                            fontWeight: 700,
                                                            color: '#1e293b',
                                                            fontSize: '0.9rem',
                                                        }}
                                                    >
                                                        {points.toLocaleString()}
                                                    </Typography>
                                                </TableCell>

                                                {/* Level */}
                                                <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                                    {level ? (
                                                        <Chip
                                                            label={level}
                                                            size="small"
                                                            sx={{
                                                                fontFamily,
                                                                fontWeight: 600,
                                                                fontSize: '0.7rem',
                                                                bgcolor: '#f1f5f9',
                                                                color: '#475569',
                                                                height: 24,
                                                            }}
                                                        />
                                                    ) : (
                                                        <Typography sx={{ fontFamily, color: '#cbd5e1', fontSize: '0.8rem' }}>-</Typography>
                                                    )}
                                                </TableCell>

                                                {/* Badges */}
                                                <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                                    {badgesCount > 0 ? (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                                            <MilitaryTechIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
                                                            <Typography sx={{ fontFamily, fontWeight: 600, color: '#475569', fontSize: '0.85rem' }}>
                                                                {badgesCount}
                                                            </Typography>
                                                        </Box>
                                                    ) : (
                                                        <Typography sx={{ fontFamily, color: '#cbd5e1', fontSize: '0.8rem' }}>0</Typography>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                )}
            </Box>
        </Box>
    );
}
